import React, { useState } from 'react';
import { Layout, Typography, Row, Col, Card, Statistic, Select, Slider, Button, Space, Divider } from 'antd';
import { ReloadOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Pie, Column, Line } from '@ant-design/charts';
import './Dashboard.css';
import { sectoralEmissionsData, powerSectorEmissionsByFuel, historicalPowerEmissions, dataSourceInfo } from '../data/egyptGHGData';

const { Header, Content, Footer } = Layout;
const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const Dashboard: React.FC = () => {
  // State for filters
  const [selectedSectors, setSelectedSectors] = useState<string[]>(['Electricity and Heat']);
  const [yearRange, setYearRange] = useState<[number, number]>([2010, 2021]);
  const [selectedFuels, setSelectedFuels] = useState<string[]>(['Natural Gas', 'Oil', 'Coal']);

  // Filter data based on selections
  const filteredSectoralData = sectoralEmissionsData.filter(item => 
    selectedSectors.length === 0 || selectedSectors.includes(item.sector)
  );

  const filteredFuelData = powerSectorEmissionsByFuel.filter(item => 
    selectedFuels.length === 0 || selectedFuels.includes(item.fuel)
  );

  const filteredHistoricalData = historicalPowerEmissions.filter(item => 
    item.year >= yearRange[0] && item.year <= yearRange[1]
  );

  // Calculate totals for statistics
  const totalPowerEmissions = sectoralEmissionsData.find(item => item.sector === 'Electricity and Heat')?.value || 0;
  const totalEmissions = sectoralEmissionsData.reduce((sum, item) => sum + item.value, 0);
  const powerSectorPercentage = (totalPowerEmissions / totalEmissions) * 100;
  
  // Year-over-year change calculation
  const currentYearEmissions = historicalPowerEmissions[historicalPowerEmissions.length - 1].value;
  const previousYearEmissions = historicalPowerEmissions[historicalPowerEmissions.length - 2].value;
  const yearOverYearChange = ((currentYearEmissions - previousYearEmissions) / previousYearEmissions) * 100;

  // Chart configurations
  const sectorPieConfig = {
    data: filteredSectoralData,
    angleField: 'value',
    colorField: 'sector',
    radius: 0.8,
    label: {
      type: 'outer',
      content: '{name}: {percentage}',
    },
    interactions: [{ type: 'element-active' }],
  };

  const fuelBarConfig = {
    data: filteredFuelData,
    xField: 'fuel',
    yField: 'value',
    label: {
      position: 'middle',
      style: {
        fill: '#FFFFFF',
        opacity: 0.6,
      },
    },
    meta: {
      value: {
        alias: 'Emissions (Mt CO₂e)',
      },
    },
  };

  const historicalLineConfig = {
    data: filteredHistoricalData,
    xField: 'year',
    yField: 'value',
    point: {
      size: 5,
      shape: 'diamond',
    },
    label: {
      style: {
        fill: '#aaa',
      },
    },
  };

  // Reset all filters
  const handleReset = () => {
    setSelectedSectors(['Electricity and Heat']);
    setYearRange([2010, 2021]);
    setSelectedFuels(['Natural Gas', 'Oil', 'Coal']);
  };

  // Handle sector selection in pie chart
  const handleSectorSelect = (sector: string) => {
    if (selectedSectors.includes(sector)) {
      setSelectedSectors(selectedSectors.filter(s => s !== sector));
    } else {
      setSelectedSectors([...selectedSectors, sector]);
    }
  };

  // Handle fuel selection in bar chart
  const handleFuelSelect = (fuel: string) => {
    if (selectedFuels.includes(fuel)) {
      setSelectedFuels(selectedFuels.filter(f => f !== fuel));
    } else {
      setSelectedFuels([...selectedFuels, fuel]);
    }
  };

  return (
    <Layout className="layout" style={{ minHeight: '100vh' }}>
      <Header style={{ background: '#fff', padding: '0 20px' }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={3} style={{ margin: '16px 0' }}>
              Egypt Power Sector GHG Emissions Dashboard
            </Title>
          </Col>
          <Col>
            <Space>
              <Button 
                icon={<ReloadOutlined />} 
                onClick={handleReset}
              >
                Reset Filters
              </Button>
              <Button 
                icon={<InfoCircleOutlined />} 
                type="text"
                title="Data Source Information"
              >
                Info
              </Button>
            </Space>
          </Col>
        </Row>
      </Header>
      
      <Content style={{ padding: '0 50px', marginTop: 20 }}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Card title="Filters">
              <Row gutter={[16, 16]}>
                <Col span={8}>
                  <Text strong>Sectors:</Text>
                  <Select
                    mode="multiple"
                    style={{ width: '100%' }}
                    placeholder="Select sectors"
                    value={selectedSectors}
                    onChange={setSelectedSectors}
                  >
                    {sectoralEmissionsData.map(item => (
                      <Option key={item.sector} value={item.sector}>{item.sector}</Option>
                    ))}
                  </Select>
                </Col>
                <Col span={8}>
                  <Text strong>Year Range:</Text>
                  <Slider
                    range
                    min={2010}
                    max={2021}
                    value={yearRange}
                    onChange={(value) => setYearRange(value as [number, number])}
                  />
                </Col>
                <Col span={8}>
                  <Text strong>Fuel Types:</Text>
                  <Select
                    mode="multiple"
                    style={{ width: '100%' }}
                    placeholder="Select fuel types"
                    value={selectedFuels}
                    onChange={setSelectedFuels}
                  >
                    {powerSectorEmissionsByFuel.map(item => (
                      <Option key={item.fuel} value={item.fuel}>{item.fuel}</Option>
                    ))}
                  </Select>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col span={6}>
            <Card>
              <Statistic
                title="Total Power Sector Emissions"
                value={totalPowerEmissions}
                precision={2}
                suffix="Mt CO₂e"
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="% of National Emissions"
                value={powerSectorPercentage}
                precision={1}
                suffix="%"
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Year-over-Year Change"
                value={yearOverYearChange}
                precision={1}
                valueStyle={{ color: yearOverYearChange >= 0 ? '#cf1322' : '#3f8600' }}
                prefix={yearOverYearChange >= 0 ? '+' : ''}
                suffix="%"
              />
            </Card>
          </Col>
          <Col span={6}>
            <Card>
              <Statistic
                title="Per Capita Power Emissions"
                value={totalPowerEmissions / 104}
                precision={2}
                suffix="t CO₂e"
              />
              <Text type="secondary">Based on ~104 million population</Text>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col span={12}>
            <Card title="Sectoral Breakdown of GHG Emissions">
              <Pie {...sectorPieConfig} onReady={(plot) => {
                plot.on('element:click', (evt: any) => {
                  const { sector } = evt.data.data;
                  handleSectorSelect(sector);
                });
              }} />
            </Card>
          </Col>
          <Col span={12}>
            <Card title="Historical Power Sector Emissions Trend">
              <Line {...historicalLineConfig} />
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col span={24}>
            <Card title="Power Sector Emissions by Fuel Type">
              <Column {...fuelBarConfig} onReady={(plot) => {
                plot.on('element:click', (evt: any) => {
                  const { fuel } = evt.data.data;
                  handleFuelSelect(fuel);
                });
              }} />
            </Card>
          </Col>
        </Row>

        <Divider />

        <Row>
          <Col span={24}>
            <Card title="About This Dashboard">
              <Paragraph>
                This interactive dashboard visualizes greenhouse gas (GHG) emissions data for Egypt's power sector, 
                with a focus on electricity and heat generation. The data is sourced from Our World in Data and 
                shows that the power sector is the largest contributor to Egypt's GHG emissions.
              </Paragraph>
              <Paragraph>
                <strong>Cross-filtering:</strong> Click on sectors in the pie chart or fuel types in the bar chart 
                to filter the data across all visualizations. Use the filters at the top to further refine the view.
              </Paragraph>
              <Paragraph>
                <strong>Data Source:</strong> {dataSourceInfo.source} ({dataSourceInfo.url}), 
                accessed on {dataSourceInfo.accessed}
              </Paragraph>
            </Card>
          </Col>
        </Row>
      </Content>
      
      <Footer style={{ textAlign: 'center' }}>
        Egypt Power Sector GHG Dashboard ©2025 Created with React, TypeScript, and Ant Design
      </Footer>
    </Layout>
  );
};

export default Dashboard;
